/**
 * CMS Event Handlers
 * Manages language switching and tab navigation for CMS pages
 */

// Cache busting - force fresh load
const initCacheBusting = () => {
  if (performance.navigation.type === 1) {
    // Page was reloaded
    const currentUrl = window.location.href.split("?")[0];
    const timestamp = new Date().getTime();
    if (!window.location.search.includes("v=")) {
      window.location.href = currentUrl + "?v=" + timestamp;
    }
  }
};

export const attachLanguageSwitchingEvents = (locale) => {
  const cmsPageContainer = document.querySelector(".almaneaCMSPage");
  if (!cmsPageContainer) return { langBtns: [], handleLangClick: null };

  const langBtns = Array.from(cmsPageContainer.querySelectorAll(".lang-btn"));

  if (locale === "ar") {
    const langBtnsContainer = langBtns[0]?.parentElement;
    if (langBtnsContainer) {
      langBtnsContainer.setAttribute("dir", "ltr");
    }
  }

  const handleLangClick = function (e) {
    e.preventDefault();

    const lang = this.dataset.lang;
    const direction = lang === "ar" ? "rtl" : "ltr";

    langBtns.forEach((b) => b.classList.remove("active"));
    this.classList.add("active");

    const mainContent = document.querySelector(
      '[data-content-type="html"][data-appearance="default"][data-element="main"]',
    );
    if (mainContent) {
      mainContent.setAttribute("dir", direction);
      mainContent.setAttribute("lang", lang);
    }

    if (lang === "ar") {
      const langBtnsContainer = langBtns[0]?.parentElement;
      if (langBtnsContainer) {
        langBtnsContainer.setAttribute("dir", "ltr");
      }
    }

    cmsPageContainer.querySelectorAll("[data-ar][data-en]").forEach((el) => {
      el.textContent = el.dataset[lang];
    });
  };

  langBtns.forEach((btn) => {
    btn.addEventListener("click", handleLangClick);
  });

  // Set initial active language button based on locale
  const activeBtn = langBtns.find((btn) => btn.dataset.lang === locale);
  if (activeBtn) {
    activeBtn.click();
  }

  return { langBtns, handleLangClick };
};

export const attachTabSwitchingEvents = () => {
  const cmsPageContainer = document.querySelector(".almaneaCMSPage");
  if (!cmsPageContainer)
    return { tabBtns: [], handleTabClick: null, handleKeyboardNav: null };

  const tabBtns = cmsPageContainer.querySelectorAll(".tab-btn");
  const tabContents = cmsPageContainer.querySelectorAll(".tab-content");
  const html = document.documentElement;

  const handleTabClick = function (e) {
    e.preventDefault();

    const tabId = this.dataset.tab;

    tabBtns.forEach((b) => b.classList.remove("active"));
    this.classList.add("active");

    tabContents.forEach((content) => {
      content.classList.remove("active");
      if (content.id === tabId) {
        content.classList.add("active");
      }
    });

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleKeyboardNav = (e) => {
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      const activeTab = document.querySelector(".tab-btn.active");
      const allTabs = Array.from(tabBtns);
      const currentIndex = allTabs.indexOf(activeTab);

      let newIndex;
      if (e.key === "ArrowRight") {
        newIndex =
          html.getAttribute("dir") === "rtl"
            ? (currentIndex - 1 + allTabs.length) % allTabs.length
            : (currentIndex + 1) % allTabs.length;
      } else {
        newIndex =
          html.getAttribute("dir") === "rtl"
            ? (currentIndex + 1) % allTabs.length
            : (currentIndex - 1 + allTabs.length) % allTabs.length;
      }

      allTabs[newIndex].click();
    }
  };

  tabBtns.forEach((btn) => {
    btn.addEventListener("click", handleTabClick);
  });

  document.addEventListener("keydown", handleKeyboardNav);

  return { tabBtns, handleTabClick, handleKeyboardNav };
};

// Active/inactive Tailwind class sets for the Tawuniya T&C tab buttons — mirror
// the CMS markup so the clicked button gets the highlighted (navy) style.
const TW_TAB_ACTIVE = [
  "bg-[#00269a]",
  "campaign:bg-campaignAccent",
  "font-bold",
  "text-white",
];
const TW_TAB_INACTIVE = [
  "bg-[rgba(136,133,133,0.05)]",
  "font-semibold",
  "text-black",
  "hover:text-[#00269a]",
  "campaign:hover:text-campaignAccent",
];

// The Tawuniya T&C CMS page toggles two panels (.ewstc-content / .adstc-content)
// via the Tailwind `hidden` class. Its buttons carry no data-tab (the generic
// .tab-btn wiring doesn't apply), and only the first button is tagged (.ewstc) —
// the second (Accidental Damage) is resolved as the other button in the tab bar.
export const attachTawuniyaTermsTabEvents = () => {
  const cmsPageContainer = document.querySelector(".almaneaCMSPage");
  if (!cmsPageContainer) return { cleanup: null };

  const ewContent = cmsPageContainer.querySelector(".ewstc-content");
  const adContent = cmsPageContainer.querySelector(".adstc-content");
  const ewBtn = cmsPageContainer.querySelector("button.ewstc");
  if (!ewContent || !adContent || !ewBtn) return { cleanup: null };

  const adBtn = Array.from(
    ewBtn.parentElement?.querySelectorAll("button") || []
  ).find((btn) => btn !== ewBtn);
  if (!adBtn) return { cleanup: null };

  const activate = (activePanel, hiddenPanel, activeBtn, inactiveBtn) => {
    activePanel.classList.remove("hidden");
    hiddenPanel.classList.add("hidden");
    activeBtn.classList.remove(...TW_TAB_INACTIVE);
    activeBtn.classList.add(...TW_TAB_ACTIVE);
    inactiveBtn.classList.remove(...TW_TAB_ACTIVE);
    inactiveBtn.classList.add(...TW_TAB_INACTIVE);
  };

  const showEw = () => activate(ewContent, adContent, ewBtn, adBtn);
  const showAd = () => activate(adContent, ewContent, adBtn, ewBtn);

  ewBtn.addEventListener("click", showEw);
  adBtn.addEventListener("click", showAd);

  return {
    cleanup: () => {
      ewBtn.removeEventListener("click", showEw);
      adBtn.removeEventListener("click", showAd);
    },
  };
};

export const setupCmsEvents = (locale) => {
  initCacheBusting();

  const { langBtns, handleLangClick } = attachLanguageSwitchingEvents(locale);
  const { tabBtns, handleTabClick, handleKeyboardNav } =
    attachTabSwitchingEvents();
  const { cleanup: cleanupTawuniyaTabs } = attachTawuniyaTermsTabEvents();

  return () => {
    langBtns.forEach((btn) => {
      btn.removeEventListener("click", handleLangClick);
    });

    tabBtns.forEach((btn) => {
      btn.removeEventListener("click", handleTabClick);
    });

    document.removeEventListener("keydown", handleKeyboardNav);

    if (cleanupTawuniyaTabs) cleanupTawuniyaTabs();
  };
};
